import React, { useState } from 'react';
import {
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Box,
  Typography,
} from '@mui/material';
import MoreIcon from '@mui/icons-material/MoreVert';
import ArchiveIcon from '@mui/icons-material/Archive';
import DuplicateIcon from '@mui/icons-material/FileCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import type { Session } from 'logic/session';

interface SessionItemProps {
  session: Session;
  onClick: () => void;
  archiveSession: (sessionId: string) => void;
  deleteSession: (sessionId: string) => void;
  duplicateSession: (sessionId: string) => void;
  isCurrentSession?: boolean;
}

export const SessionItem = ({
  session,
  onClick,
  archiveSession,
  deleteSession,
  duplicateSession,
  isCurrentSession = false,
}: SessionItemProps) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const menuOpen = !!menuAnchor;

  const toggleMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation(); // Prevent triggering the ListItem click
    setMenuAnchor(menuAnchor ? null : e.currentTarget);
  };
  const closeMenu = () => setMenuAnchor(null);

  // Keep hovered state true when menu is open to prevent unmounting
  const handleMouseLeave = () => {
    if (!menuOpen) {
      setIsHovered(false);
    }
  };

  const handleDuplicate = () => {
    closeMenu();
    duplicateSession(session.id);
  };

  const handleArchive = () => {
    closeMenu();
    archiveSession(session.id);
  };

  const handleDelete = () => {
    closeMenu();
    deleteSession(session.id);
  };

  // Show the action button when hovered or menu is open
  const showActionButton = !session.data.deletedAt && (isHovered || menuOpen);

  return (
    <Box onMouseEnter={() => setIsHovered(true)} onMouseLeave={handleMouseLeave}>
      <ListItem
        button
        dense
        onClick={onClick}
        sx={{
          bgcolor: isHovered ? 'action.hover' : 'transparent',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        {session.data.isArchived && !session.data.deletedAt && (
          <ListItemIcon>
            <CheckCircleIcon htmlColor="#008466" />
          </ListItemIcon>
        )}
        <ListItemText>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{session.data.name}</span>
            {isCurrentSession && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                }}
              >
                (Current)
              </Typography>
            )}
          </Box>
        </ListItemText>
        {showActionButton && (
          <ListItemSecondaryAction>
            <IconButton
              size="small"
              onClick={toggleMenu}
              sx={{
                '&:hover': {
                  bgcolor: 'action.selected',
                },
              }}
            >
              <MoreIcon />
            </IconButton>
            <Menu
              open={menuOpen}
              anchorEl={menuAnchor}
              onClose={closeMenu}
              anchorReference="anchorEl"
              anchorOrigin={{
                horizontal: 'left',
                vertical: 'bottom',
              }}
            >
              <MenuItem onClick={handleDuplicate}>
                <ListItemIcon>
                  <DuplicateIcon />
                </ListItemIcon>
                <ListItemText>Duplicate Project</ListItemText>
              </MenuItem>
              {!session.data.isArchived && (
                <MenuItem onClick={handleArchive}>
                  <ListItemIcon>
                    <ArchiveIcon />
                  </ListItemIcon>
                  <ListItemText>Archive Project</ListItemText>
                </MenuItem>
              )}
              <MenuItem onClick={handleDelete}>
                <ListItemIcon>
                  <DeleteIcon />
                </ListItemIcon>
                <ListItemText>Trash Project</ListItemText>
              </MenuItem>
            </Menu>
          </ListItemSecondaryAction>
        )}
      </ListItem>
    </Box>
  );
};
